import { type FC, type RefObject, useCallback } from 'react';
import cn from 'classnames';
import { Button, HStack, Stub, Text, VStack } from '@direct-frontend/components';
import { type Optional } from '@direct-frontend/stdlib';
import { useFormContext, useFormInstance } from '@adv-frontend/form-kit';
import { useLoadingOrder } from '@/entities/order';
import { Sticky } from '@/shared/components/Sticky';
import { ORDER_FORM_ID } from '../../constants';
import { orderForm, validateOrderForm } from '../../model/forms/orderForm';
import styles from './PaymentInformerStickyButton.module.css';

interface PaymentInformerStickyButton {
    submitErrorMessage: Optional<string>;
    fullPrice: Optional<string>;
    isPending: boolean;
    buttonTextWithPrice: string;
    isNativePayEnabled: boolean;
    widgetRef: RefObject<HTMLElement>;
    containerRef: RefObject<HTMLElement>;
}

export const PaymentInformerStickyButton: FC<PaymentInformerStickyButton> = (props) => {
    const {
        submitErrorMessage,
        fullPrice,
        isPending,
        buttonTextWithPrice,
        isNativePayEnabled,
        widgetRef,
        containerRef,
    } = props;

    const context = useFormContext({ form: orderForm, id: ORDER_FORM_ID });
    const { submit, scrollToFirstError } = useFormInstance({ form: orderForm, id: ORDER_FORM_ID });

    const { isOrderSideUpdating } = useLoadingOrder();

    const handleSubmit = useCallback(() => {
        if (isNativePayEnabled && widgetRef.current) {
            const formInstance = orderForm.getInstance(ORDER_FORM_ID);

            validateOrderForm(formInstance.getValue());

            const isValid = orderForm.getInstance(ORDER_FORM_ID).getState().isValid;

            if (!isValid) {
                scrollToFirstError();

                return;
            }

            widgetRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        } else {
            submit();
        }
    }, [isNativePayEnabled, scrollToFirstError, submit, widgetRef]);

    return (
        <Sticky threshold={0.5} target={containerRef}>
            {({ isSticky, stickStyle, style }) => (
                <div className={cn(isSticky ? stickStyle : style, styles.stickyFooter)}>
                    <VStack gap="8">
                        <HStack justifyContent="space-between">
                            <Text size="l" bold>
                                Итого
                            </Text>
                            {context.deliveryOptions.isLoading || isOrderSideUpdating ? (
                                <Stub height={28} width={100} />
                            ) : (
                                <Text size="l" bold>
                                    {fullPrice}
                                </Text>
                            )}
                        </HStack>
                        <VStack gap="4">
                            <VStack gap="12" alignItems="center">
                                <Button
                                    size="m"
                                    width="max"
                                    color="accent"
                                    isDisabled={isPending}
                                    isLoading={isPending}
                                    onClick={handleSubmit}
                                >
                                    {buttonTextWithPrice}
                                </Button>
                            </VStack>
                            {submitErrorMessage && (
                                <Text size="xs" color="red" className={styles.error}>
                                    {submitErrorMessage}
                                </Text>
                            )}
                        </VStack>
                    </VStack>
                </div>
            )}
        </Sticky>
    );
};
